import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useMemo, useState } from "react";
import { formatPercent, formatMonthDay } from "@/utils/format";
import * as echarts from "echarts";
import { useBrandComparison } from "@/hooks/useBrandComparison";
import { calculatePercentageYAxis } from "@/utils/chartHelpers";
import { MetricType } from "@/services/api/brandComparison";

interface BrandComparisonChartProps {
  xLabels?: string[];
  series?: Array<{
    name: string;
    values: number[];
    color?: string;
    isCurrentBrand?: boolean;
  }>;
  data?: Array<{ brand: string; values: number[] }>;
  selectedKeys?: string[];
  brandId?: string;
  brandName?: string;
  categoryId?: string;
  days?: number;
  currentMetric?: string;
  onMetricChange?: (metric: string) => void;
  showBrandSelector?: boolean;
  onBrandRefresh?: (
    brands: Array<{
      brandId: string;
      brandName: string;
      color: string;
      isCurrentBrand: boolean;
    }>
  ) => void;
  brands?: Array<{
    brandId: string;
    brandName: string;
    color: string;
    isCurrentBrand: boolean;
  }>;
  selectedCompetitors?: string[];
  onCompetitorChange?: (competitors: string[]) => void;
  showBrandButtons?: boolean;
  metricInfo?: {
    key: string;
    title: string;
    tooltip: string;
  };
  brandComparison?: any; // 从searchFlow传入的品牌对比数据
}

export const BrandComparisonChart: React.FC<BrandComparisonChartProps> = ({
  xLabels,
  series,
  brandId,
  brandName,
  categoryId,
  days = 7,
  currentMetric = "mentionRate",
  onBrandRefresh,
  brands,
  selectedCompetitors = [],
  onCompetitorChange,
  showBrandButtons = false,
  metricInfo,
  brandComparison, // 新增：接收brandComparison属性
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstRef = useRef<echarts.ECharts | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipMessage, setTooltipMessage] = useState("");

  // 数据转换函数 - 将rateList格式转换为series格式
  const convertRateListToSeries = (
    rateList: any,
    brandList: any,
    metric: string
  ) => {
    if (!rateList || !brandList || !rateList[metric]) {
      return [];
    }

    const series = [];
    const allBrands = [brandList.curBrand, ...(brandList.otherBrands || [])];

    // 为每个品牌创建系列
    for (const brand of allBrands) {
      if (!brand || !rateList[metric][brand.brandId]) continue;

      const brandData = rateList[metric][brand.brandId];
      const values = brandData.map((item: any) => item.rate);

      series.push({
        name: brand.brandName,
        values: values,
        isCurrentBrand: brand.brandId === brandList.curBrand?.brandId,
      });
    }

    return series;
  };

  // 生成X轴标签
  const generateXLabelsFromRateList = (rateList: any, days: number) => {
    if (!rateList || Object.keys(rateList).length === 0) {
      return [];
    }

    // 从任意一个品牌的任意一个指标中提取日期
    const firstMetric = Object.keys(rateList)[0];
    const firstBrand = Object.keys(rateList[firstMetric])[0];
    const firstBrandData = rateList[firstMetric][firstBrand];

    if (!firstBrandData || firstBrandData.length === 0) {
      // 如果没有数据，生成默认标签
      const labels = [];
      const endDate = new Date();
      for (let i = 0; i < days; i++) {
        const date = new Date(endDate);
        date.setDate(endDate.getDate() - (days - 1 - i));
        labels.push(formatMonthDay(date));
      }
      return labels;
    }

    // 从数据中提取日期
    return firstBrandData.map((item: any) => {
      return formatMonthDay(item.day);
    });
  };

  // 转换品牌列表格式
  const convertBrandListToBrands = (brandList: any) => {
    if (!brandList) return [];

    const allBrands = [brandList.curBrand, ...(brandList.otherBrands || [])];
    return allBrands
      .filter((brand) => brand)
      .map((brand, index) => ({
        brandId: brand.brandId,
        brandName: brand.brandName,
        color: getBrandColor(brand.brandName, index),
        isCurrentBrand: brand.brandId === brandList.curBrand?.brandId,
      }));
  };

  // 统一的颜色配置
  const brandColors = [
    "#1890ff", // 蓝色 - 理想
    "#52c41a", // 绿色 - 蔚来
    "#FF7F7F", // 红色 - 比亚迪
    "#fa8c16", // 橙色 - 小鹏
    "#722ed1", // 紫色 - 特斯拉
    "#13c2c2", // 青色 - 其他品牌
    "#eb2f96", // 粉色
    "#f5222d", // 深红
    "#fadb14", // 黄色
    "#2f54eb", // 深蓝
  ];

  // 获取品牌颜色 - 使用统一颜色序列
  const getBrandColor = (_brandName: string, index: number) => {
    return brandColors[index % brandColors.length] || "#95A5A6";
  };

  // 使用品牌对比 Hook - 总是调用，但优先使用传入的brandComparison数据
  const brandComparisonResult = useBrandComparison({
    brandId: brandId || "",
    brandName: brandName || "",
    categoryId: categoryId || "",
    days,
    initialData: brandComparison, // 传递从searchFlow传入的数据
  });

  // 内部状态管理 - 选中的品牌名称
  const [selectedBrandNames, setSelectedBrandNames] = useState<string[]>([]);

  // 获取最终数据 - 优先使用传入的brandComparison数据，其次使用hook数据，最后使用默认数据
  const finalSeries = useMemo(() => {
    let result = [];

    // 1. 优先使用brandComparison的series数据
    if (brandComparison?.series?.length) {
      result = brandComparison.series;
    }
    // 2. 如果有rateList数据，尝试转换
    else if (
      brandComparison?.rateList &&
      Object.keys(brandComparison.rateList).length > 0
    ) {
      result = convertRateListToSeries(
        brandComparison.rateList,
        brandComparison.brandList,
        currentMetric
      );
    }
    // 3. 使用hook返回的数据
    else if (brandComparisonResult.chartData?.series?.length) {
      result = brandComparisonResult.chartData.series;
    }
    // 4. 最后使用默认的series数据
    else {
      result = series || [];
    }

    // 确保result是数组
    if (!Array.isArray(result)) {
      result = [];
    }

    // 基于内部状态来过滤显示 - 确保主品牌始终包含
    const currentMainBrandName =
      brandComparison?.brandList?.curBrand?.brandName || brandName || "";
    const allSelectedNames = [
      currentMainBrandName,
      ...selectedBrandNames,
    ].filter(Boolean);
    const selectedBrands = result.filter(
      (s) => s && s.name && allSelectedNames.includes(s.name)
    );

    // 确保主品牌始终在图表中显示（即使没有被选中）
    const mainBrandSeries = result.find(
      (s) => s && s.name === currentMainBrandName
    );
    if (
      mainBrandSeries &&
      !selectedBrands.some((s) => s.name === currentMainBrandName)
    ) {
      selectedBrands.unshift(mainBrandSeries);
    }

    return selectedBrands;
  }, [
    brandComparison?.series,
    brandComparison?.rateList,
    brandComparison?.brandList,
    currentMetric,
    brandComparisonResult.chartData?.series,
    series,
    selectedBrandNames,
    brandName,
  ]);

  const finalXLabels = (() => {
    // 1. 优先使用brandComparison的xLabels数据
    if (brandComparison?.xLabels?.length) {
      return brandComparison.xLabels;
    }

    // 2. 如果有rateList数据，尝试转换
    if (
      brandComparison?.rateList &&
      Object.keys(brandComparison.rateList).length > 0
    ) {
      return generateXLabelsFromRateList(brandComparison.rateList, days);
    }

    // 3. 使用hook返回的数据
    if (brandComparisonResult.chartData?.xLabels?.length) {
      return brandComparisonResult.chartData.xLabels;
    }

    // 4. 最后使用默认的xLabels数据
    return xLabels;
  })();

  const finalBrands = useMemo(() => {
    // 优先使用brandComparison中的品牌数据
    if (brandComparison?.brandList) {
      return convertBrandListToBrands(brandComparison.brandList);
    }
    // 其次使用传入的brands
    if (brands) {
      return brands;
    }
    // 最后使用hook返回的数据
    return brandComparisonResult.chartData?.brands || [];
  }, [
    brandComparison?.brandList,
    brands,
    brandComparisonResult.chartData?.brands,
  ]);

  // 选中的品牌按钮数据 - 与finalSeries一一对应
  const finalSelectedBrands = useMemo(() => {
    if (!finalBrands || finalBrands.length === 0) {
      return [];
    }

    // 基于内部状态 + 当前实际主品牌
    const currentMainBrandName =
      brandComparison?.brandList?.curBrand?.brandName || brandName || "";
    const allSelectedNames = [
      currentMainBrandName,
      ...selectedBrandNames,
    ].filter(Boolean);
    return finalBrands.filter(
      (b: any) => b && b.brandName && allSelectedNames.includes(b.brandName)
    );
  }, [
    finalBrands,
    selectedBrandNames,
    brandComparison?.brandList?.curBrand?.brandName,
    brandName,
  ]);

  // 设置初始选中品牌（只在组件首次加载时执行一次）
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (finalBrands && finalBrands.length > 0 && !hasInitialized) {
      // 选择前3个竞品品牌（使用当前实际的主品牌名称）
      const currentMainBrandName =
        brandComparison?.brandList?.curBrand?.brandName || brandName || "";
      const competitorBrands = finalBrands.filter(
        (brand: any) =>
          !brand.isCurrentBrand && brand.brandName !== currentMainBrandName
      );
      const initialBrands = competitorBrands
        .slice(0, 3)
        .map((brand: any) => brand.brandName);
      if (initialBrands.length > 0) {
        setSelectedBrandNames(initialBrands);
        setHasInitialized(true);
      }
    }
  }, [
    finalBrands,
    hasInitialized,
    brandComparison?.brandList?.curBrand?.brandName,
    brandName,
  ]);

  // 数据源更新时的强制刷新逻辑
  const [lastBrandComparisonKey, setLastBrandComparisonKey] =
    useState<string>("");
  useEffect(() => {
    if (!brandComparison) return;

    // 生成当前数据的唯一标识 - 包含主品牌信息
    const currentKey = JSON.stringify({
      brandList: brandComparison.brandList,
      rateListKeys: brandComparison.rateList
        ? Object.keys(brandComparison.rateList)
        : [],
      curBrandId: brandComparison.brandList?.curBrand?.brandId,
    });

    // 如果数据源发生变化，强制重置选择状态
    if (lastBrandComparisonKey && currentKey !== lastBrandComparisonKey) {
      // 重置为初始选择状态（前3个竞品）
      if (finalBrands && finalBrands.length > 0) {
        const currentMainBrandName =
          brandComparison?.brandList?.curBrand?.brandName || brandName || "";
        const competitorBrands = finalBrands.filter(
          (brand: any) =>
            !brand.isCurrentBrand && brand.brandName !== currentMainBrandName
        );
        const initialBrands = competitorBrands
          .slice(0, 3)
          .map((brand: any) => brand.brandName);
        if (initialBrands.length > 0) {
          setSelectedBrandNames(initialBrands);
        }
      }

      // 重置初始化状态，确保重新初始化
      setHasInitialized(false);
    }

    // 更新最后的数据标识
    setLastBrandComparisonKey(currentKey);
  }, [brandComparison, finalBrands, brandName, lastBrandComparisonKey]);

  // 清洗外部传入的 series，避免 undefined 造成图例或系列报错
  const safeSeries = Array.isArray(finalSeries)
    ? finalSeries
        .filter(
          (
            s
          ): s is {
            name: string;
            values: number[];
            color?: string;
            isCurrentBrand?: boolean;
          } => !!s && typeof s.name === "string" && Array.isArray(s.values)
        )
        .map((s) => ({
          ...s,
          values: s.values.map((v) => (Number.isFinite(v) ? v : 0)),
        }))
    : [];

  // 处理图表显示数据 - 直接使用finalSeries
  const chartSeries = useMemo(() => {
    return finalSeries;
  }, [finalSeries]);

  // 自动调用品牌刷新回调
  useEffect(() => {
    if (onBrandRefresh && finalBrands && finalBrands.length > 0) {
      onBrandRefresh(finalBrands);
    }
  }, [finalBrands, onBrandRefresh]);

  // X轴标签
  const computedXLabels =
    finalXLabels && finalXLabels.length
      ? finalXLabels
      : ["1/10", "2/10", "3/10", "4/10", "5/10", "6/10", "7/10"];

  // 图表配置
  const chartOption = {
    title: { show: false },
    tooltip: {
      trigger: "axis",
      formatter: (params: any[]) => {
        const axis =
          params?.[0]?.axisValueLabel || params?.[0]?.axisValue || "";
        const head = axis
          ? `<div style="margin-bottom:4px;font-weight:600;">${axis}</div>`
          : "";
        const body = (params || [])
          .map((p: any) => {
            const val =
              typeof p?.value === "number" ? p.value : Number(p?.value);
            const text = Number.isFinite(val) ? formatPercent(val, 1) : "--%";
            return `${p.marker} ${p.seriesName}: ${text}`;
          })
          .join("<br/>");
        return head + body;
      },
      axisPointer: {
        type: "line",
        snap: true,
        lineStyle: { type: "dashed", width: 2, color: "#6b5bd2" },
      },
      backgroundColor: "rgba(255,255,255,0.95)",
      borderColor: "#e5e7eb",
      textStyle: { color: "#0f172a" },
    },
    grid: {
      left: 5,
      right: 20,
      bottom: 30,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: computedXLabels,
      splitLine: { show: false },
      axisLine: { lineStyle: { color: "#e5e7eb" } },
      axisTick: { show: false },
      axisLabel: { color: "#6b7280" },
    },
    yAxis: (() => {
      const { min, max } = calculatePercentageYAxis(chartSeries);
      return {
        type: "value",
        min,
        max,
        axisLabel: { formatter: (val: number) => formatPercent(val, 1) },
        splitLine: {
          show: true,
          lineStyle: { type: "dashed", color: "#e5e7eb" },
        },
      };
    })(),
    series: chartSeries.map((s, index) => {
      // 确保图表颜色与按钮颜色一致：优先使用finalBrands中的颜色
      const brandData = finalBrands?.find((b: any) => b.brandName === s.name);
      const color =
        brandData?.color || s.color || brandColors[index % brandColors.length];
      const isCurrentBrand = s.isCurrentBrand || false;
      return {
        name: s.name,
        type: "line",
        smooth: true,
        data: s.values,
        itemStyle: { color },
        lineStyle: {
          color,
          width: isCurrentBrand ? 3 : 2,
        },
        symbol: "circle",
        symbolSize: isCurrentBrand ? 8 : 6,
        areaStyle: {
          color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: echarts.color.modifyAlpha(
                color,
                isCurrentBrand ? 0.15 : 0.08
              ),
            },
            { offset: 1, color: echarts.color.modifyAlpha(color, 0.02) },
          ]),
        },
      };
    }),
  };

  // 初始化图表
  useEffect(() => {
    if (chartRef.current && !chartInstRef.current) {
      chartInstRef.current = echarts.init(chartRef.current);
      const handleResize = () => chartInstRef.current?.resize();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        chartInstRef.current?.dispose();
        chartInstRef.current = null;
      };
    }
  }, []);

  // 更新图表
  useEffect(() => {
    if (chartInstRef.current) {
      chartInstRef.current.setOption(chartOption, true);
      chartInstRef.current.resize();
    }
  }, [chartSeries, computedXLabels]);

  // 指标切换
  useEffect(() => {
    if (brandComparisonResult.switchMetric && currentMetric) {
      brandComparisonResult.switchMetric(currentMetric as MetricType);
    }
  }, [currentMetric, brandComparisonResult.switchMetric]);

  return (
    <div style={{ width: "100%" }}>
      {/* 品牌选择按钮 */}
      {showBrandButtons && (
        <div
          style={{
            padding: "16px 20px",
            background:
              "linear-gradient(to bottom, rgba(248,250,252,0.3), rgba(248,250,252,0.8))",
            borderBottom: "1px solid #f1f5f9",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              justifyContent: "center",
            }}
          >
            {finalBrands && finalBrands.length > 0 ? (
              finalBrands
                // 确保按钮顺序与数据源一致：主品牌优先，然后按数据源中的顺序
                .sort((a: any, b: any) => {
                  // 主品牌排在最前面
                  if (a.isCurrentBrand && !b.isCurrentBrand) return -1;
                  if (!a.isCurrentBrand && b.isCurrentBrand) return 1;
                  // 其他品牌保持数据源中的原始顺序
                  return 0;
                })
                .map((brandData: any) => {
                  const isMainBrand = brandData.isCurrentBrand === true;
                  // 基于finalSelectedBrands判断选中状态
                  const isSelected = finalSelectedBrands.some(
                    (b: any) => b.brandName === brandData.brandName
                  );

                  // 调试日志：打印当前品牌信息
                  // console.log('🔍 品牌信息调试:', {
                  //   brandName: brandData.brandName,
                  //   isCurrentBrand: brandData.isCurrentBrand,
                  //   isMainBrand,
                  //   currentBrandName: brandName,
                  //   isSelected
                  // })

                  return (
                    <button
                      key={brandData.brandId}
                      onClick={(e) => {
                        if (isMainBrand) return;

                        // 判断是否已选中
                        const isCurrentlySelected = finalSelectedBrands.some(
                          (b: any) => b.brandName === brandData.brandName
                        );

                        if (isCurrentlySelected) {
                          // 取消选择：从内部状态中移除
                          setSelectedBrandNames((prev) =>
                            prev.filter((c) => c !== brandData.brandName)
                          );
                        } else {
                          // 检查是否已经达到最大选择数量
                          const currentSelectedCount =
                            finalSelectedBrands.length;
                          if (currentSelectedCount >= 5) {
                            // 显示自定义tooltip提示
                            const buttonRect =
                              e.currentTarget.getBoundingClientRect();
                            setTooltipPosition({
                              x: buttonRect.left + buttonRect.width / 2,
                              y: buttonRect.top - 10,
                            });
                            setTooltipMessage(
                              t("common.charts.brand_compare_limit_tip")
                            );
                            setTooltipVisible(true);

                            // 3秒后自动隐藏
                            setTimeout(() => {
                              setTooltipVisible(false);
                            }, 3000);
                            return;
                          }

                          // 添加选择
                          setSelectedBrandNames((prev) => [
                            ...prev,
                            brandData.brandName,
                          ]);
                        }
                      }}
                      style={{
                        padding: "4px 10px",
                        border: "none",
                        borderRadius: 20,
                        background: isSelected
                          ? brandData.color
                          : "rgba(255,255,255,0.8)",
                        color: isSelected ? "#ffffff" : "#64748b",
                        fontSize: 12,
                        fontWeight: isSelected ? 600 : 500,
                        cursor: isMainBrand ? "default" : "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: isSelected
                          ? `0 2px 8px ${brandData.color}30`
                          : "0 1px 3px rgba(0,0,0,0.1)",
                        transform: isMainBrand ? "scale(1.2)" : "scale(1)",
                        minWidth: "60px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {brandData.brandName}
                    </button>
                  );
                })
            ) : (
              <div style={{ color: "#6b7280", fontSize: 14 }}>
                {t("common.charts.brand_compare_no_data")}
              </div>
            )}
          </div>
          {finalBrands && finalBrands.length > 0 && (
            <div
              style={{
                marginTop: 12,
                textAlign: "center",
                fontSize: 12,
                color: "#666666",
              }}
            >
              {t("common.charts.brand_compare_limit_tip")}
            </div>
          )}
        </div>
      )}

      {/* 图表容器 */}
      <div style={{ width: "100%", height: "400px", padding: "2px" }}>
        <div ref={chartRef} style={{ width: "100%", height: "400px" }} />

        {brandComparisonResult.loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              color: "#6b7280",
            }}
          >
            {t("search_detail.competitor_loading")}
          </div>
        )}
      </div>

      {/* 自定义 Tooltip */}
      {tooltipVisible && (
        <div
          style={{
            position: "fixed",
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: "translateX(-50%) translateY(-100%)",
            background: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "8px 12px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            zIndex: 9999,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            pointerEvents: "none",
          }}
        >
          {tooltipMessage}
          <div
            style={{
              position: "absolute",
              bottom: "-4px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "4px solid rgba(0, 0, 0, 0.8)",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default BrandComparisonChart;
